// ---------------------------------------------------------------------------
// Group sub-basins by zone/snow zone/outlet point/aquifer, for the no-GIS
// canvas (SubBasinCanvas.tsx) and any other view that wants to show them
// organized this way instead of by geometry.
// ---------------------------------------------------------------------------
import type { Aquifer, OutletPoint, SubBasin, Zone } from "./types.ts";

interface ZoneGroup {
  /** null = sub-basins with no zone assigned. */
  zone: Zone | null;
  basins: SubBasin[];
}

function groupBasinsByField(
  basins: SubBasin[],
  zones: Zone[],
  zoneField: "zoneCode" | "snowZoneCode",
  modelTypeField: "modelTypeCode" | "snowModelTypeCode"
): ZoneGroup[] {
  const byZone = new Map<number | null, SubBasin[]>();
  for (const c of basins) {
    const key = c[zoneField];
    const list = byZone.get(key);
    if (list) list.push(c);
    else byZone.set(key, [c]);
  }

  // Every zone of the requested type shows up, whether or not it already has
  // sub-basins assigned — a newly created, still-empty zone has to be able
  // to receive sub-basins dragged onto it. The "no zone" group ALSO always
  // shows up (even empty) and comes FIRST — SubBasinCanvas.tsx's tabbed
  // layout shows it as the always-present leftmost "unassigned" drop target
  // (drag here to unassign), not just an incidental leftover bucket.
  const unassigned: ZoneGroup = { zone: null, basins: byZone.get(null) ?? [] };
  const rest: ZoneGroup[] = zones
    .filter((z) => z[modelTypeField] != null)
    .map((zone) => ({ zone, basins: byZone.get(zone.zoneCode) ?? [] }));
  return [unassigned, ...rest];
}

/** Groups by runoff zone (zoneCode). */
export function groupBasinsByZone(basins: SubBasin[], zones: Zone[]): ZoneGroup[] {
  return groupBasinsByField(basins, zones, "zoneCode", "modelTypeCode");
}

/**
 * Groups by snow zone (snowZoneCode) — independent of groupBasinsByZone: a
 * sub-basin can have both a runoff zone and a snow zone at the same time, so
 * it can appear in a group from each function at once, it's not exclusive.
 */
export function groupBasinsBySnowZone(basins: SubBasin[], zones: Zone[]): ZoneGroup[] {
  return groupBasinsByField(basins, zones, "snowZoneCode", "snowModelTypeCode");
}

interface EntityGroup<E> {
  /** null = sub-basins with none assigned (outletCode === null, aquiferCode === 0). */
  entity: E | null;
  basins: SubBasin[];
}

/**
 * Same shape/reasoning as `groupBasinsByField` above, but for outlet points
 * and aquifers. `fieldValueOf` is expected to normalize its own field's
 * "unassigned" sentinel to `null` (outletCode already is `null`;
 * aquiferCode still uses `0`, see SubBasin, types.ts) so this shared
 * function only ever deals with one sentinel. Kept as a separate generic
 * instead of unifying with `groupBasinsByField`: the "does this zone/entity
 * even apply" filter above (`z[modelTypeField] != null`) has no equivalent
 * here — every outlet point/aquifer in the project is always a valid
 * group, there's no "kind" to filter by.
 */
function groupBasinsByEntityCode<E>(
  basins: SubBasin[],
  entities: E[],
  codeOf: (entity: E) => number,
  fieldValueOf: (subBasin: SubBasin) => number | null
): EntityGroup<E>[] {
  const byCode = new Map<number | null, SubBasin[]>();
  for (const c of basins) {
    const key = fieldValueOf(c);
    const list = byCode.get(key);
    if (list) list.push(c);
    else byCode.set(key, [c]);
  }
  const unassigned: EntityGroup<E> = { entity: null, basins: byCode.get(null) ?? [] };
  const rest: EntityGroup<E>[] = entities.map((entity) => ({
    entity,
    basins: byCode.get(codeOf(entity)) ?? [],
  }));
  return [unassigned, ...rest];
}

/** Groups by surface outlet point (outletCode). */
export function groupBasinsByOutlet(
  basins: SubBasin[],
  outletPoints: OutletPoint[]
): EntityGroup<OutletPoint>[] {
  return groupBasinsByEntityCode(
    basins,
    outletPoints,
    (p) => p.outletCode,
    (c) => c.outletCode
  );
}

/** Groups by outlet aquifer (aquiferCode). */
export function groupBasinsByAquifer(
  basins: SubBasin[],
  aquifers: Aquifer[]
): EntityGroup<Aquifer>[] {
  return groupBasinsByEntityCode(
    basins,
    aquifers,
    (a) => a.aquiferCode,
    (c) => (c.aquiferCode === 0 ? null : c.aquiferCode)
  );
}
